// Workflow builder options - Sequential step-based system

export const TRIGGER_OPTIONS = [
  {
    id: "gap_discovered",
    name: "Gap discovered",
    description: "When a new security gap is identified",
    type: "security",
    nextConditions: [
      "severity_check",
      "tool_check",
      "assignee_check",
      "duration_check",
    ],
  },
  {
    id: "high_severity_gap",
    name: "High-Severity Gap",
    description: "When a high severity gap is detected",
    type: "security",
    nextConditions: ["tool_check", "asset_type_check", "assignee_check"],
  },
  {
    id: "attacker_activity",
    name: "Attacker Activity",
    description: "When suspicious attacker activity is detected",
    type: "threat",
    nextConditions: ["asset_type_check", "severity_check"],
  },
  {
    id: "detection_events",
    name: "Detection Events",
    description: "When security detection events occur",
    type: "detection",
    nextConditions: ["severity_check", "asset_type_check"],
  },
  {
    id: "asset_discovered",
    name: "Asset discovered",
    description: "When a new asset is discovered in the environment",
    type: "inventory",
    nextConditions: ["asset_type_check"],
  },
];

// Condition types - first level selection
export const CONDITION_TYPES = [
  {
    id: "severity_is",
    name: "Severity is",
    description: "Check the severity level",
    type: "severity",
  },
  {
    id: "tool_is",
    name: "Tool is",
    description: "Check the source tool",
    type: "tool",
  },
  {
    id: "asset_type_is",
    name: "Asset type is",
    description: "Check the asset type",
    type: "asset",
  },
  {
    id: "has_assignee",
    name: "Has assignee",
    description: "Check if there's an assignee",
    type: "assignment",
  },
  {
    id: "duration_is",
    name: "Duration is",
    description: "Check how long it has existed",
    type: "time",
  },
];

// Condition options - second level selection based on type
export const CONDITION_OPTIONS = {
  severity_is: [
    { id: "high", name: "High", description: "High", type: "HIGH" },
    { id: "medium", name: "Medium", description: "Medium", type: "MEDIUM" },
    { id: "low", name: "Low", description: "Low", type: "LOW" },
    {
      id: "critical",
      name: "Critical",
      description: "Critical",
      type: "CRITICAL",
    },
  ],
  tool_is: [
    { id: "okta", name: "Okta", description: "Okta", type: "OKTA" },
    {
      id: "crowdstrike",
      name: "CrowdStrike",
      description: "CrowdStrike",
      type: "CROWDSTRIKE",
    },
    { id: "aws", name: "AWS", description: "AWS", type: "AWS" },
    { id: "jamf", name: "Jamf", description: "Jamf", type: "JAMF" },
  ],
  asset_type_is: [
    {
      id: "Key People",
      name: "Key People",
      description: "Admins",
      type: "Key People",
    },
    {
      id: "Internal Application",
      name: "Internal Application",
      description: "Apps",
      type: "Internal Application",
    },
    {
      id: "External Application",
      name: "External Application",
      description: "Web Apps",
      type: "External Application",
    },
    {
      id: "Sensitive Data",
      name: "Sensitive Data",
      description: "PII",
      type: "Sensitive Data",
    },
    {
      id: "Other",
      name: "Other",
      description: "Other",
      type: "Other",
    },
  ],
  has_assignee: [
    { id: "yes", name: "Yes", description: "Yes", type: "HAS_ASSIGNEE" },
    { id: "no", name: "No", description: "No", type: "NO_ASSIGNEE" },
  ],
  duration_is: [
    { id: "1_day", name: "Over 1 day", description: "1", type: "1" },
    { id: "7_days", name: "Over 7 days", description: "7", type: "7" },
    { id: "14_days", name: "Over 14 days", description: "14", type: "14" },
    { id: "30_days", name: "Over 30 days", description: "30", type: "30" },
    { id: "90_days", name: "Over 90 days", description: "90", type: "90" },
  ],
};

export const ACTION_OPTIONS = [
  {
    id: "create_ticket",
    name: "Create ticket",
    description: "Create a ticket in your ticketing system",
    type: "ticketing",
    config: {
      project: {
        type: "select",
        options: ["Security Engineering", "IT Operations", "DevOps"],
      },
      issueType: { type: "select", options: ["Epic", "Story", "Task", "Bug"] },
      summary: { type: "text", placeholder: "Issue Summary is Taken from gap" },
      description: {
        type: "text",
        placeholder: "Issue description is Taken from Gap",
      },
      assignee: { type: "select", options: ["Team-member", "Manager", "CISO"] },
      labels: {
        type: "multi-select",
        options: ["crowdstrike", "jamf", "okta", "breeze"],
      },
    },
  },
  {
    id: "send_slack",
    name: "Send Slack Message",
    description: "Send a notification to Slack channel",
    type: "notification",
    config: {
      channel: { type: "select", options: ["#security", "#alerts", "#devops"] },
      message: { type: "text", placeholder: "Custom message" },
      mentions: {
        type: "multi-select",
        options: ["@channel", "@here", "@security-team"],
      },
    },
  },
  {
    id: "send_email",
    name: "Email CISO",
    description: "Send email notification to CISO",
    type: "notification",
    config: {
      recipient: {
        type: "select",
        options: ["CISO", "Security Team", "IT Manager"],
      },
      subject: { type: "text", placeholder: "Email subject" },
      template: {
        type: "select",
        options: ["Security Alert", "Gap Escalation", "Custom"],
      },
    },
  },
  {
    id: "webhook",
    name: "Call Webhook",
    description: "Call external webhook with gap data",
    type: "integration",
    config: {
      url: { type: "text", placeholder: "https://api.example.com/webhook" },
      method: { type: "select", options: ["POST", "PUT", "PATCH"] },
      headers: { type: "key-value", placeholder: "Custom headers" },
    },
  },
];

// Helper functions for sequential workflow building
export const getAvailableConditions = (selectedTrigger) => {
  // For now, return all condition types - can be filtered based on trigger later
  return CONDITION_TYPES;
};

export const getConditionOptions = (conditionType) => {
  if (!conditionType || !CONDITION_OPTIONS[conditionType.id]) {
    return [];
  }
  return CONDITION_OPTIONS[conditionType.id];
};

export const getAvailableActions = (selectedCondition) => {
  return ACTION_OPTIONS;
};

export const getTriggerById = (id) => {
  return TRIGGER_OPTIONS.find((trigger) => trigger.id === id);
};

export const getConditionTypeById = (id) => {
  return CONDITION_TYPES.find((type) => type.id === id);
};

export const getConditionById = (typeId, optionId) => {
  const options = CONDITION_OPTIONS[typeId];
  if (!options) return null;
  return options.find((option) => option.id === optionId);
};

export const getActionById = (id) => {
  return ACTION_OPTIONS.find((action) => action.id === id);
};
